import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Send, Copy, Check, ShieldAlert, Mail, MailX, CircleCheck, CircleAlert } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import { getRevenueSummary, getWebhookDeliveries, resendWebhookDelivery } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const STATUS_STYLES = {
  success: "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]",
  test: "bg-[color:rgba(180,83,9,0.12)] text-[#a16207]",
  duplicate: "bg-[color:rgba(37,99,235,0.12)] text-[#2563eb]",
  failed: "bg-[color:rgba(220,38,38,0.12)] text-[#dc2626]",
  unverified: "bg-[color:rgba(11,18,32,0.1)] text-[color:rgba(11,18,32,0.6)]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

const formatMoney = (amountCents, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format((Number(amountCents) || 0) / 100);
  } catch (e) {
    return `${currency || "USD"} ${((Number(amountCents) || 0) / 100).toFixed(2)}`;
  }
};

function StatusPill({ ready, label }) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
        ready
          ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
          : "bg-[color:rgba(180,83,9,0.12)] text-[#a16207]",
      ].join(" ")}
    >
      {ready ? <CircleCheck className="h-3.5 w-3.5" /> : <CircleAlert className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function RevenueSummary({ summary, loading }) {
  if (loading) {
    return <div className="mb-6 h-28 animate-pulse rounded-lg bg-[color:rgba(10,27,51,0.06)]" />;
  }

  const revenue = summary?.gross_revenue || [];
  const automation = summary?.automation || {};
  const grossSales = revenue.length
    ? revenue.map((entry) => formatMoney(entry.amount_cents, entry.currency)).join(" + ")
    : "$0.00";

  return (
    <div data-testid={STUDY.adminRevenueSummary} className="mb-6 grid gap-3 lg:grid-cols-[1.4fr_1fr]">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">Paid orders</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--ibp-ink)]">{summary?.paid_orders || 0}</p>
        </div>
        <div className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">Gross sales</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--ibp-ink)]">{grossSales}</p>
        </div>
        <div className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">Account emails sent</p>
          <p className="mt-1 text-2xl font-semibold text-[#15803d]">{summary?.email_delivered || 0}</p>
        </div>
        <div className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">Needs attention</p>
          <p className={[
            "mt-1 text-2xl font-semibold",
            summary?.needs_email_attention ? "text-[#B42318]" : "text-[var(--ibp-ink)]",
          ].join(" ")}>{summary?.needs_email_attention || 0}</p>
        </div>
      </div>

      <div data-testid={STUDY.adminFulfillmentStatus} className="rounded-lg border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">Automatic fulfillment</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <StatusPill ready={automation.webhook_secret_ready} label={automation.webhook_secret_ready ? "Webhook secret ready" : "Webhook secret missing"} />
          <StatusPill ready={automation.email_sender_ready} label={automation.email_sender_ready ? "Email sender ready" : "Email sender missing"} />
          <StatusPill ready={automation.delivery_ready} label={automation.delivery_ready ? "Ready to deliver" : "Setup incomplete"} />
        </div>
        <p className="mt-3 text-xs text-[color:rgba(11,18,32,0.6)]">
          {automation.paid_order_received
            ? `Last paid order received ${fmtDate(summary?.last_paid_at)}.`
            : "A successful paid order will confirm the Shopify connection here."}
        </p>
      </div>
    </div>
  );
}

function CopyCode({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-[color:rgba(11,18,32,0.4)]">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={copy}
      title="Copy code"
      className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 font-mono text-xs hover:bg-[color:rgba(10,27,51,0.06)]"
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-[#15803d]" /> : <Copy className="h-3.5 w-3.5 opacity-50" />}
    </button>
  );
}

function DeliveriesTable({ rows, onChanged }) {
  const [busyId, setBusyId] = useState(null);

  const resend = async (row) => {
    setBusyId(row.id);
    try {
      const res = await resendWebhookDelivery(row.id);
      if (res.email_sent) {
        toast.success("Account start email resent", { description: res.recipient });
      } else {
        toast.warning("Resend attempted, but email delivery failed", {
          description: "Check email logs / Resend domain verification.",
        });
      }
      onChanged();
    } catch (e) {
      toast.error("Resend failed", { description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
      <table className="w-full text-left text-sm">
        <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
          <tr>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3">Topic</th>
            <th className="px-4 py-3">Order</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Recovery code</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3 text-center">Resent</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const canResend = Boolean(d.customer_email);
            return (
              <tr
                key={d.id}
                data-testid={STUDY.adminWebhookDeliveryRow}
                className="border-t border-[color:rgba(10,27,51,0.08)] align-top"
              >
                <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.6)]">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">
                  {d.topic || "—"}
                  {d.hmac_valid === false && (
                    <span
                      title="HMAC signature was invalid"
                      className="ml-1 inline-flex items-center gap-0.5 rounded bg-[color:rgba(220,38,38,0.12)] px-1.5 py-0.5 text-[10px] font-semibold text-[#dc2626]"
                    >
                      <ShieldAlert className="h-3 w-3" /> unverified
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">
                  {d.order_number ? <div className="font-medium">{d.order_number}</div> : null}
                  {d.shopify_order_id ? (
                    <div className="text-[11px] text-[color:rgba(11,18,32,0.5)]">{d.shopify_order_id}</div>
                  ) : (
                    !d.order_number && "—"
                  )}
                </td>
                <td className="px-4 py-3 font-medium text-[color:rgba(11,18,32,0.8)]">
                  {d.payload_summary?.total
                    ? formatMoney(d.payload_summary.total * 100, d.payload_summary.currency)
                    : "—"}
                </td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">{d.customer_email || "—"}</td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      "inline-block rounded-full px-2.5 py-1 text-xs font-semibold capitalize",
                      STATUS_STYLES[d.status] || STATUS_STYLES.unverified,
                    ].join(" ")}
                  >
                    {d.status}
                  </span>
                  {d.error ? (
                    <div className="mt-1 max-w-[220px] text-[11px] text-[color:rgba(11,18,32,0.5)]">{d.error}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <CopyCode value={d.access_code} />
                </td>
                <td className="px-4 py-3">
                  {d.email_sent ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#15803d]">
                      <Mail className="h-3.5 w-3.5" /> sent
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[color:rgba(11,18,32,0.55)]">
                      <MailX className="h-3.5 w-3.5" /> not sent
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center font-semibold">{d.resent_count || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end">
                    <button
                      data-testid={STUDY.adminWebhookResend}
                      onClick={() => resend(d)}
                      disabled={!canResend || busyId === d.id}
                      title={
                        canResend
                          ? "Resend the account start email"
                          : "No customer email to resend"
                      }
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.18)] px-3 py-1.5 text-xs font-semibold text-[var(--ibp-ink)] transition-colors hover:bg-[color:rgba(10,27,51,0.05)] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {busyId === d.id ? "Sending…" : "Resend"}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-10 text-center text-sm text-[color:rgba(11,18,32,0.5)]">
                No webhook deliveries yet. Paid checkout events will appear here once received.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeliveriesManager() {
  const qc = useQueryClient();
  const { data: deliveries = [], isLoading } = useQuery({
    queryKey: ["admin-webhook-deliveries"],
    queryFn: getWebhookDeliveries,
  });
  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["admin-revenue-summary"],
    queryFn: getRevenueSummary,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-webhook-deliveries"] });
    qc.invalidateQueries({ queryKey: ["admin-revenue-summary"] });
  };

  const counts = useMemo(() => {
    const c = { success: 0, duplicate: 0, failed: 0, unverified: 0 };
    deliveries.forEach((d) => {
      c[d.status] = (c[d.status] || 0) + 1;
    });
    return c;
  }, [deliveries]);

  return (
    <div>
      <RevenueSummary summary={summary} loading={isSummaryLoading} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
          {isLoading
            ? "Loading..."
            : `${deliveries.length} delivery(ies) — ${counts.success} success · ${counts.duplicate} duplicate · ${counts.failed} failed · ${counts.unverified} unverified`}
        </p>
        <button
          onClick={refresh}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-4 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <DeliveriesTable rows={deliveries} onChanged={refresh} />

      <p className="mt-3 text-[11px] text-[color:rgba(11,18,32,0.5)]">
        {`Each paid checkout order grants full account access and keeps one hidden recovery code during migration (deduped by order id). Use "Resend" to send the buyer a fresh account-start email.`}
      </p>
    </div>
  );
}

export default function AdminWebhookDeliveries() {
  return (
    <div data-testid={STUDY.adminWebhookDeliveriesPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Webhook deliveries"
            subtitle="Audit paid checkout webhooks, confirm account access was granted, and resend the buyer's start email."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <DeliveriesManager />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
